import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { CONDITIE_VELDEN, VARIABELEN } from "@/lib/campagneStappen";
import { ProductLijst, FASES } from "@/components/campagne/ProductLijst";

export function KenmerkKiezer({ categorie, waarde, onChange, definities, nieuwToestaan = true, testid = "kenmerk" }) {
  const def = (definities || []).find(d => d.categorie === categorie);
  const [vrij, setVrij] = React.useState(false);
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Select value={categorie || "_"} onValueChange={v => onChange({ categorie: v === "_" ? "" : v, waarde: "" })}>
        <SelectTrigger className="w-52 h-8 text-xs" data-testid={`${testid}-categorie`}><SelectValue placeholder="Categorie" /></SelectTrigger>
        <SelectContent className="max-h-72"><SelectItem value="_">— categorie —</SelectItem>{(definities || []).map(d => <SelectItem key={d.id} value={d.categorie}>{d.categorie}</SelectItem>)}</SelectContent>
      </Select>
      {categorie && !vrij && (
        <Select value={waarde || "_"} onValueChange={v => { if (v === "__nieuw") { setVrij(true); onChange({ categorie, waarde: "" }); } else onChange({ categorie, waarde: v === "_" ? "" : v }); }}>
          <SelectTrigger className="w-56 h-8 text-xs" data-testid={`${testid}-waarde`}><SelectValue placeholder="Waarde" /></SelectTrigger>
          <SelectContent className="max-h-72"><SelectItem value="_">— waarde —</SelectItem>{(def?.waarden || []).map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}
            {nieuwToestaan && <SelectItem value="__nieuw">+ nieuwe waarde…</SelectItem>}</SelectContent>
        </Select>
      )}
      {categorie && vrij && <Input className="w-56 h-8 text-xs" autoFocus value={waarde || ""} onChange={e => onChange({ categorie, waarde: e.target.value })} placeholder="nieuwe waarde" data-testid={`${testid}-waarde-nieuw`} onBlur={() => { if (!waarde) setVrij(false); }} />}
    </div>
  );
}

const TALEN = ["nl", "en", "de"];

function Veld({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function VariabelenChips({ onPick }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid="variabelen-chips">
      {VARIABELEN.map(v => (
        <button key={v} type="button" onClick={() => onPick(v)} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-muted hover:bg-primary/10 hover:text-primary transition-colors">{v}</button>
      ))}
    </div>
  );
}

function OntwerpKiezer({ config, onChange, ontwerpen, doel, testid }) {
  const lijst = (ontwerpen || []).filter(o => o.doel === doel || doel === "campagne");
  return (
    <Veld label="HTML-ontwerp (blokken-builder, huisstijl nextproduct.nl)" hint={<>Beheer ontwerpen onder <a className="text-primary underline" href="/instellingen/email-ontwerpen" target="_blank" rel="noreferrer">Instellingen → E-mailontwerpen</a>.</>}>
      <Select value={config.design_id || "_"} onValueChange={v => onChange({ ...config, design_id: v === "_" ? null : v })}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="_">{doel === "campagne" ? "— platte tekst (hieronder) —" : "— standaard uitnodigingsmail —"}</SelectItem>{lijst.map(o => <SelectItem key={o.id} value={o.id}>{o.naam}{o.standaard ? " ★" : ""}</SelectItem>)}</SelectContent>
      </Select>
    </Veld>
  );
}

function EmailConfig({ config, onChange, afzenders, stapId, ontwerpen }) {
  const [taal, setTaal] = React.useState("nl");
  const teksten = config.teksten || {};
  const t = teksten[taal] || { onderwerp: "", tekst: "" };
  const setT = (k, v) => onChange({ ...config, teksten: { ...teksten, [taal]: { ...t, [k]: v } } });
  const ref = React.useRef(null);
  const voegIn = (v) => {
    const el = ref.current; const pos = el?.selectionStart ?? t.tekst.length;
    setT("tekst", (t.tekst || "").slice(0, pos) + v + (t.tekst || "").slice(pos));
  };
  if (config.design_id) {
    return (
      <div className="space-y-3">
        <OntwerpKiezer config={config} onChange={onChange} ontwerpen={ontwerpen} doel="campagne" testid="email-ontwerp" />
        <p className="text-xs text-muted-foreground">Onderwerp, teksten per taal en afmeldlink komen uit het ontwerp; variabelen worden per deelnemer ingevuld.</p>
        <Veld label="Afzender (leeg = campagne-afzender)">
          <Select value={config.afzender_id || "_"} onValueChange={v => onChange({ ...config, afzender_id: v === "_" ? null : v })}>
            <SelectTrigger data-testid="email-afzender"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="_">— campagne-afzender —</SelectItem>{afzenders.map(a => <SelectItem key={a.id} value={a.id}>{a.naam} ({a.email})</SelectItem>)}</SelectContent>
          </Select>
        </Veld>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <OntwerpKiezer config={config} onChange={onChange} ontwerpen={ontwerpen} doel="campagne" testid="email-ontwerp" />
      <div className="flex items-center gap-1">
        {TALEN.map(l => (
          <button key={l} type="button" onClick={() => setTaal(l)} data-testid={`email-taal-${l}`}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${taal === l ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"} ${teksten[l]?.tekst ? "" : "opacity-60"}`}>
            {l.toUpperCase()}{teksten[l]?.tekst ? " ✓" : ""}
          </button>
        ))}
        <span className="text-[11px] text-muted-foreground ml-2">NL is verplicht; andere talen vallen terug op NL.</span>
      </div>
      <Veld label={`Onderwerp (${taal})`}>
        <Input value={t.onderwerp || ""} onChange={e => setT("onderwerp", e.target.value)} data-testid="email-onderwerp" placeholder="Bijv. Bestel nu direct bij {{afzender.naam}}, {{organisatie.naam}}" />
      </Veld>
      <Veld label={`Tekst (${taal})`} hint="Platte tekst; regels worden regels. {{afmeld_link}} is verplicht.">
        <Textarea ref={ref} rows={10} value={t.tekst || ""} onChange={e => setT("tekst", e.target.value)} data-testid="email-tekst" className="font-mono text-xs" />
      </Veld>
      <VariabelenChips onPick={voegIn} />
      <Veld label="Afzender (leeg = campagne-afzender)">
        <Select value={config.afzender_id || "_"} onValueChange={v => onChange({ ...config, afzender_id: v === "_" ? null : v })}>
          <SelectTrigger data-testid="email-afzender"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_">— campagne-afzender —</SelectItem>{afzenders.map(a => <SelectItem key={a.id} value={a.id}>{a.naam} ({a.email})</SelectItem>)}</SelectContent>
        </Select>
      </Veld>
    </div>
  );
}

function UitnodigingConfig({ config, onChange, afzenders, ontwerpen }) {
  return (
    <div className="space-y-3">
      <OntwerpKiezer config={config} onChange={onChange} ontwerpen={ontwerpen} doel="uitnodiging" testid="uitnodiging-ontwerp" />
      <p className="text-xs text-muted-foreground">Gebruikt de bestaande uitnodigingsmail (taal per land, persoonlijke activatielink). Heeft de organisatie al een actief account → stap wordt overgeslagen; open uitnodiging → opnieuw verstuurd.</p>
      <Veld label="Aanbodtekst (extra alinea in de mail)">
        <Textarea rows={3} value={config.aanbodtekst || ""} onChange={e => onChange({ ...config, aanbodtekst: e.target.value })} data-testid="uitnodiging-aanbod" placeholder="Gratis verzending, geen minimum, op rekening, POS-materiaal bij je eerste order." />
      </Veld>
      <div className="grid grid-cols-2 gap-3">
        <Veld label="Vervalt na (dagen)"><Input type="number" value={config.vervalt_dagen ?? 30} onChange={e => onChange({ ...config, vervalt_dagen: Number(e.target.value) })} data-testid="uitnodiging-vervalt" /></Veld>
        <Veld label="Afzender">
          <Select value={config.afzender_id || "_"} onValueChange={v => onChange({ ...config, afzender_id: v === "_" ? null : v })}>
            <SelectTrigger data-testid="uitnodiging-afzender"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="_">— campagne-afzender —</SelectItem>{afzenders.map(a => <SelectItem key={a.id} value={a.id}>{a.naam}</SelectItem>)}</SelectContent>
          </Select>
        </Veld>
      </div>
    </div>
  );
}

function WachtConfig({ config, onChange }) {
  const soort = config.soort || "werkdagen";
  return (
    <div className="grid grid-cols-2 gap-3">
      <Veld label="Soort">
        <Select value={soort} onValueChange={v => onChange({ ...config, soort: v })}>
          <SelectTrigger data-testid="wacht-soort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="werkdagen">Werkdagen (ma–vr)</SelectItem><SelectItem value="kalenderdagen">Kalenderdagen</SelectItem>
            <SelectItem value="uren">Uren</SelectItem><SelectItem value="datum">Tot datum/tijd</SelectItem><SelectItem value="weekdag">Tot weekdag + tijd</SelectItem>
          </SelectContent>
        </Select>
      </Veld>
      {["werkdagen", "kalenderdagen", "uren"].includes(soort) && <Veld label="Duur"><Input type="number" min={1} value={config.duur ?? 1} onChange={e => onChange({ ...config, duur: Number(e.target.value) })} data-testid="wacht-duur" /></Veld>}
      {soort === "datum" && <Veld label="Datum/tijd"><Input type="datetime-local" value={(config.datum || "").slice(0, 16)} onChange={e => onChange({ ...config, datum: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="wacht-datum" /></Veld>}
      {soort === "weekdag" && (<>
        <Veld label="Weekdag">
          <Select value={String(config.weekdag ?? 1)} onValueChange={v => onChange({ ...config, weekdag: Number(v) })}>
            <SelectTrigger data-testid="wacht-weekdag"><SelectValue /></SelectTrigger>
            <SelectContent>{["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"].map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </Veld>
        <Veld label="Tijd"><Input type="time" value={config.tijd || "09:30"} onChange={e => onChange({ ...config, tijd: e.target.value })} data-testid="wacht-tijd" /></Veld>
      </>)}
    </div>
  );
}

function EnkeleConditie({ c, onChange, onRemove, stappen, uitkomsten, definities }) {
  const def = CONDITIE_VELDEN.find(v => v.veld === c.veld) || CONDITIE_VELDEN[0];
  const set = (k, v) => onChange({ ...c, [k]: v });
  const toggleLijst = (w) => { const cur = Array.isArray(c.waarde) ? c.waarde : []; set("waarde", cur.includes(w) ? cur.filter(x => x !== w) : [...cur, w]); };
  return (
    <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg border border-border bg-background" data-testid="conditie-rij">
      <Select value={c.veld} onValueChange={v => onChange({ veld: v, operator: ["getal"].includes(CONDITIE_VELDEN.find(x => x.veld === v)?.soort) ? "groter" : "is", waarde: CONDITIE_VELDEN.find(x => x.veld === v)?.soort === "meerkeuze" ? [] : "" })}>
        <SelectTrigger className="w-56 h-8 text-xs" data-testid="conditie-veld"><SelectValue /></SelectTrigger>
        <SelectContent>{CONDITIE_VELDEN.map(v => <SelectItem key={v.veld} value={v.veld}>{v.label}</SelectItem>)}</SelectContent>
      </Select>
      {def.soort === "getal" && (<>
        <Select value={c.operator || "groter"} onValueChange={v => set("operator", v)}>
          <SelectTrigger className="w-24 h-8 text-xs" data-testid="conditie-operator"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="groter">groter dan</SelectItem><SelectItem value="kleiner">kleiner dan</SelectItem></SelectContent>
        </Select>
        <Input type="number" className="w-28 h-8 text-xs" value={c.waarde ?? ""} onChange={e => set("waarde", Number(e.target.value))} data-testid="conditie-waarde" />
      </>)}
      {(def.soort === "bool" || def.soort === "stap" || def.soort === "tekst" || def.soort === "tekstlijst" || def.soort === "meerkeuze" || def.soort === "stap-uitkomst") && (
        <Select value={c.operator === "niet" ? "niet" : "is"} onValueChange={v => set("operator", v)}>
          <SelectTrigger className="w-24 h-8 text-xs" data-testid="conditie-operator"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="is">is / ja</SelectItem><SelectItem value="niet">niet / nee</SelectItem></SelectContent>
        </Select>
      )}
      {(def.soort === "stap" || def.soort === "stap-uitkomst") && (
        <Select value={c.stap_id || "_"} onValueChange={v => set("stap_id", v === "_" ? null : v)}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="conditie-stap"><SelectValue placeholder="stap" /></SelectTrigger>
          <SelectContent><SelectItem value="_">elke eerdere stap</SelectItem>{stappen.filter(s => (def.soort === "stap" ? s.type === "email" : s.type === "beltaak")).map(s => <SelectItem key={s.id} value={s.id}>{s.naam}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {def.soort === "stap-uitkomst" && (
        <div className="flex flex-wrap gap-1">{(uitkomsten || []).map(u => (
          <label key={u.code} className="flex items-center gap-1 text-xs"><Checkbox checked={(c.waarde || []).includes(u.code)} onCheckedChange={() => toggleLijst(u.code)} />{u.label}</label>))}</div>
      )}
      {def.soort === "meerkeuze" && (
        <div className="flex flex-wrap gap-1">{def.opties.map(o => (
          <label key={o} className="flex items-center gap-1 text-xs"><Checkbox checked={(Array.isArray(c.waarde) ? c.waarde : []).includes(o)} onCheckedChange={() => toggleLijst(o)} data-testid={`conditie-optie-${o}`} />{o}</label>))}</div>
      )}
      {def.soort === "tekst" && c.veld === "heeft_kenmerk" && <KenmerkKiezer categorie={c.categorie} waarde={c.waarde} definities={definities} nieuwToestaan={false} testid="conditie-kenmerk" onChange={({ categorie, waarde }) => onChange({ ...c, categorie, waarde })} />}
      {def.soort === "tekst" && c.veld !== "heeft_kenmerk" && <Input className="w-40 h-8 text-xs" value={c.waarde || ""} onChange={e => set("waarde", e.target.value)} placeholder="kenmerk" data-testid="conditie-waarde" />}
      {def.soort === "tekstlijst" && <Input className="w-48 h-8 text-xs" value={Array.isArray(c.waarde) ? c.waarde.join(", ") : (c.waarde || "")} onChange={e => set("waarde", e.target.value.split(",").map(x => x.trim()).filter(Boolean))} placeholder="Nederland, België" data-testid="conditie-waarde" />}
      {onRemove && <Button type="button" variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={onRemove} data-testid="conditie-verwijder"><Trash2 className="h-3.5 w-3.5" /></Button>}
    </div>
  );
}

export function ConditieEditor({ value, onChange, stappen, uitkomsten, definities }) {
  const isGroep = value?.op && Array.isArray(value.items);
  const items = isGroep ? value.items : [value || { veld: "webshop_status", operator: "is", waarde: [] }];
  const op = isGroep ? value.op : "en";
  const emit = (lijst, nieuweOp = op) => onChange(lijst.length === 1 ? lijst[0] : { op: nieuweOp, items: lijst });
  return (
    <div className="space-y-2">
      {items.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <div className="flex items-center gap-2 pl-2">
              <Select value={op} onValueChange={v => emit(items, v)}>
                <SelectTrigger className="w-20 h-7 text-xs font-semibold" data-testid="conditie-op"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="en">EN</SelectItem><SelectItem value="of">OF</SelectItem></SelectContent>
              </Select>
            </div>
          )}
          <EnkeleConditie c={c} stappen={stappen} uitkomsten={uitkomsten} definities={definities} onChange={n => emit(items.map((x, j) => j === i ? n : x))}
            onRemove={items.length > 1 ? () => emit(items.filter((_, j) => j !== i)) : null} />
        </React.Fragment>
      ))}
      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => emit([...items, { veld: "heeft_kenmerk", operator: "is", waarde: "" }])} data-testid="conditie-toevoegen">
        <Plus className="h-3 w-3 mr-1" />Conditie (EN/OF)
      </Button>
    </div>
  );
}

function HerinneringVink({ config, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer select-none" htmlFor={testid}>
      <Checkbox id={testid} checked={config.in_herinneringen !== false} onCheckedChange={v => onChange({ ...config, in_herinneringen: v === true })} data-testid={testid} />
      <span>In Apple Herinneringen tonen <span className="text-muted-foreground">(standaard aan)</span></span>
    </label>
  );
}

function BeltaakConfig({ config, onChange, gebruikers }) {
  const ref = React.useRef(null);
  return (
    <div className="space-y-3">
      <Veld label="Reden (zichtbaar in bellijst)"><Input value={config.reden || ""} onChange={e => onChange({ ...config, reden: e.target.value })} data-testid="beltaak-reden" placeholder="Uitnodiging verstuurd, niet geactiveerd" /></Veld>
      <Veld label="Belscript (met variabelen)"><Textarea ref={ref} rows={6} value={config.script || ""} onChange={e => onChange({ ...config, script: e.target.value })} data-testid="beltaak-script" className="text-xs" /></Veld>
      <VariabelenChips onPick={v => onChange({ ...config, script: (config.script || "") + v })} />
      <div className="grid grid-cols-3 gap-3">
        <Veld label="Prioriteit">
          <Select value={config.prioriteit || "omzet_12m"} onValueChange={v => onChange({ ...config, prioriteit: v })}>
            <SelectTrigger data-testid="beltaak-prioriteit"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="omzet_12m">Omzet 12 maanden</SelectItem><SelectItem value="handmatig">Vaste waarde</SelectItem></SelectContent>
          </Select>
        </Veld>
        {config.prioriteit === "handmatig" && <Veld label="Waarde"><Input type="number" value={config.prioriteit_waarde ?? 0} onChange={e => onChange({ ...config, prioriteit_waarde: Number(e.target.value) })} data-testid="beltaak-prio-waarde" /></Veld>}
        <Veld label="Deadline (werkdagen)"><Input type="number" min={1} value={config.deadline_werkdagen ?? 3} onChange={e => onChange({ ...config, deadline_werkdagen: Number(e.target.value) })} data-testid="beltaak-deadline" /></Veld>
        <Veld label="Toewijzen aan">
          <Select value={config.toegewezen_aan || "_"} onValueChange={v => onChange({ ...config, toegewezen_aan: v === "_" ? null : v })}>
            <SelectTrigger data-testid="beltaak-toewijzen"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="_">campagne-eigenaar</SelectItem>{(gebruikers || []).map(g => <SelectItem key={g.id} value={g.id}>{g.naam || g.email}</SelectItem>)}</SelectContent>
          </Select>
        </Veld>
      </div>
      <HerinneringVink config={config} onChange={onChange} testid="beltaak-herinnering" />
      <p className="text-[11px] text-muted-foreground">Vertakking: <strong>ja</strong> = bereikt positief / terugbellen · <strong>nee</strong> = overige uitkomsten (niet bereikbaar na max. pogingen, geen interesse …). Voeg hieronder een eigen tak per uitkomst toe voor afwijkende opvolging.</p>
    </div>
  );
}

export function StapConfig({ stap, onChange, afzenders, gebruikers, stappen, uitkomsten, kenmerkDefinities, ontwerpen }) {
  const config = stap.config || {};
  const set = (c) => onChange({ ...stap, config: c });
  switch (stap.type) {
    case "email": return <EmailConfig config={config} onChange={set} afzenders={afzenders} stapId={stap.id} ontwerpen={ontwerpen} />;
    case "webshop_uitnodiging": return <UitnodigingConfig config={config} onChange={set} afzenders={afzenders} ontwerpen={ontwerpen} />;
    case "wacht": return <WachtConfig config={config} onChange={set} />;
    case "voorwaarde": return <ConditieEditor value={config.condities} onChange={v => set({ ...config, condities: v })} stappen={stappen.filter(s => s.id !== stap.id)} uitkomsten={uitkomsten} definities={kenmerkDefinities} />;
    case "verkoopkans": return (
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">Maakt per deelnemer een verkoopkans aan (zoals bij 'Lanceer' van een enkelvoudige campagne): artikelen met klantprijzen, of alleen een bedrag. Eén kans per organisatie per stap.</p>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><Veld label="Naam van de kans (leeg = campagnenaam)"><Input value={config.naam || ""} onChange={e => set({ ...config, naam: e.target.value })} placeholder="Webshop-introductie {{organisatie.naam}}" data-testid="kans-naam" /></Veld></div>
          <Veld label="Begin-fase">
            <Select value={config.fase || "Cold"} onValueChange={v => set({ ...config, fase: v })}>
              <SelectTrigger data-testid="kans-fase"><SelectValue /></SelectTrigger>
              <SelectContent>{FASES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </Veld>
        </div>
        <ProductLijst value={config.product_lijst || []} onChange={v => set({ ...config, product_lijst: v })} />
        {!(config.product_lijst || []).length && <Veld label="Fallback bedrag (€) per kans" hint="Wordt gebruikt als er geen artikelen zijn gekoppeld."><Input type="number" className="max-w-xs" value={config.template_bedrag ?? 0} onChange={e => set({ ...config, template_bedrag: Number(e.target.value) })} data-testid="kans-bedrag" /></Veld>}
        <HerinneringVink config={config} onChange={set} testid="kans-herinnering" />
      </div>);
    case "beltaak": return <BeltaakConfig config={config} onChange={set} gebruikers={gebruikers} />;
    case "kenmerk": return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-3 items-end">
          <Veld label="Actie">
            <Select value={config.actie || "toevoegen"} onValueChange={v => set({ ...config, actie: v })}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="kenmerk-actie"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="toevoegen">Toevoegen</SelectItem><SelectItem value="verwijderen">Verwijderen</SelectItem></SelectContent>
            </Select>
          </Veld>
          <Veld label="Kenmerk (categorie → waarde)">
            <KenmerkKiezer categorie={config.categorie} waarde={config.waarde} definities={kenmerkDefinities} onChange={({ categorie, waarde }) => set({ ...config, categorie, waarde })} />
          </Veld>
        </div>
        <p className="text-[11px] text-muted-foreground">Nieuwe waarden worden automatisch aan de categorie toegevoegd (Instellingen → Kenmerken).</p>
      </div>);
    case "status": return (
      <Veld label="Nieuwe organisatiestatus">
        <Select value={config.status || "klant"} onValueChange={v => set({ ...config, status: v })}>
          <SelectTrigger data-testid="status-waarde"><SelectValue /></SelectTrigger>
          <SelectContent>{["suspect", "prospect", "klant", "inactief"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Veld>);
    case "taak": return (
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2"><Veld label="Omschrijving"><Input value={config.omschrijving || ""} onChange={e => set({ ...config, omschrijving: e.target.value })} data-testid="taak-omschrijving" placeholder="Sample sturen naar {{organisatie.naam}}" /></Veld></div>
        <Veld label="Deadline (werkdagen)"><Input type="number" value={config.deadline_werkdagen ?? 3} onChange={e => set({ ...config, deadline_werkdagen: Number(e.target.value) })} data-testid="taak-deadline" /></Veld>
      </div>);
    case "stop": return <Veld label="Reden (optioneel)"><Input value={config.reden || ""} onChange={e => set({ ...config, reden: e.target.value })} data-testid="stop-reden" placeholder="Flow afgerond" /></Veld>;
    default: return null;
  }
}
